import {ModelFetchOptions} from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {getArchivePurpose} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {InaccessibleInfo, isInaccessibleInfo} from 'auth/RequestAccess';
import {NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import BaseFolderItem, {BaseFolderItemAttributes} from 'folder-items/BaseFolderItem';
import {
  BaseFolderItemFileData,
  BaseFolderItemFileDataWithNumberIDFolder,
  ThirdPartyStorageServiceData,
} from 'folder-items/FileConstants';
import normalizeFolder from 'folders/util/normalizeFolder';
import {doQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import {
  graphQLToREST,
  restArrayToGraphQL,
  restToGraphQL,
} from 'graphql-client/declarative-translations/external/translate';
import initializeWatchQuery from 'graphql-client/initializeWatchQuery';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {toNumberIDFolder, withNonNullSource} from 'nav/graphql/folder/Folder';
import {Note} from 'notebook/util/NoteUtilConstants';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {asJQueryPromise} from 'util/Promises';
import {Optional} from 'util/Types';

import {ArchiveRecordGraphQL} from '../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {InaccessibleInfoGraphQL} from '../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {BaseFolderItemFileGraphQL} from '../../graphql-fragments/nav/folder-items/__generated/BaseFolderItemFileGraphQL';

export type FolderItemOrInaccessibleGraphQL = BaseFolderItemFileGraphQL;
export type AccessibleFolderItemGraphQL = Exclude<FolderItemOrInaccessibleGraphQL, InaccessibleInfoGraphQL>;

export interface FolderItemFileQueryVariables {
  fileId: string;
}

interface ApolloFolderItemBaseAttributes<TFolderItemGraphQL extends AccessibleFolderItemGraphQL>
  extends BaseFolderItemAttributes {
  unsubscribeApollo: () => void;
  folderItem: BaseFolderItemFileData;
  graphQLFolderItem: TFolderItemGraphQL;
}

interface ApolloFolderItemBase<
  TQuery,
  TFolderItemGraphQL extends AccessibleFolderItemGraphQL,
  TFolderItemQueryVariables extends FolderItemFileQueryVariables = FolderItemFileQueryVariables,
> {
  get<K extends keyof ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>(
    key: K
  ): ApolloFolderItemBaseAttributes<TFolderItemGraphQL>[K];
  set<K extends keyof ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>(
    attributeName: K,
    value: ApolloFolderItemBaseAttributes<TFolderItemGraphQL>[K],
    options?: Backbone.ModelSetOptions
  ): ApolloFolderItemBase<TQuery, TFolderItemGraphQL, TFolderItemQueryVariables>;
  set(
    obj: Partial<ApolloFolderItemBaseAttributes<TFolderItemGraphQL>>
  ): ApolloFolderItemBase<TQuery, TFolderItemGraphQL, TFolderItemQueryVariables>;
}

abstract class ApolloFolderItemBase<
  TQuery,
  TFolderItemGraphQL extends AccessibleFolderItemGraphQL,
  TFolderItemQueryVariables extends FolderItemFileQueryVariables = FolderItemFileQueryVariables,
> extends BaseFolderItem {
  initialize(...args: Array<unknown>): void {
    super.initialize(...args);
    const querySubscription = initializeWatchQuery<TQuery>(
      {...this.getQueryWrapper(), variables: this.queryVariables()},
      this.onChange.bind(this)
    );
    this.set({
      unsubscribeApollo: () => {
        querySubscription.unsubscribe();
      },
    });
  }

  cleanUp(): void {
    this.get('unsubscribeApollo')();
  }

  private doQuery(): Promise<BaseFolderItemFileData | InaccessibleInfo> {
    return new Promise((resolve, reject) => {
      doQuerySafe<TQuery, TFolderItemQueryVariables>(this.getQueryWrapper(), {
        variables: this.queryVariables(),
        // We want to re-fetch the latest folder item data from the network every time a workspace tab is
        // opened, rather than showing potentially-outdated data from the cache (which could happen if the tab
        // had previously been opened during the same page load)
        fetchPolicy: 'network-only',
      }).then((data) => {
        const folderItemData = this.queryResultToFolderItemData(data);
        if (isInaccessibleInfo(folderItemData)) {
          reject(folderItemData);
        } else {
          resolve(folderItemData.folderItem);
        }
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetch(options: ModelFetchOptions): JQueryPromise<any> {
    return asJQueryPromise(this.doQuery());
  }

  protected abstract getQueryWrapper(): QueryWrapper<TQuery, TFolderItemQueryVariables>;

  protected abstract queryVariables(): TFolderItemQueryVariables;

  protected abstract getGraphQLFolderItemFromQueryData(
    data: TQuery
  ): TFolderItemGraphQL | InaccessibleInfoGraphQL;

  private queryResultToFolderItemData(
    data: TQuery
  ): {folderItem: BaseFolderItemFileData; graphQLFolderItem: TFolderItemGraphQL} | InaccessibleInfoGraphQL {
    const graphQLFolderItem = this.getGraphQLFolderItemFromQueryData(data);
    const folderItem = graphQLToREST('BaseFolderItemFile', graphQLFolderItem);
    if (isInaccessibleInfo(folderItem)) {
      return folderItem;
    }
    // TS cannot infer that these are not InaccessibleInfos
    const accessibleFolderItem = folderItem as BaseFolderItemFileDataWithNumberIDFolder;
    return {
      folderItem: {
        ...accessibleFolderItem,
        folder: normalizeFolder(accessibleFolderItem.folder),
      } as BaseFolderItemFileData,
      graphQLFolderItem: graphQLFolderItem as TFolderItemGraphQL,
    };
  }

  onChange(data: TQuery, loading: boolean): void {
    if (loading) {
      return;
    }
    const folderItemData = this.queryResultToFolderItemData(data);
    if (isInaccessibleInfo(folderItemData)) {
      return;
    }
    const {folderItem, graphQLFolderItem} = folderItemData;
    this.set({
      // Cache the full folderItem object to use in all the getters
      folderItem,
      // Cache the full graphQLFolderItem object to use in getUpdatedQueryData
      graphQLFolderItem,
      // This is the minimum set of properties that must live directly on the file, because FolderItemState
      // and FolderDAOCache listen to updates on them
      name: folderItem.name,
      editURL: folderItem.editURL,
      tagSchema: folderItem.tagSchema,
      folder: folderItem.folder,
    });
  }

  protected folderItem(): BaseFolderItemFileData {
    return this.get('folderItem');
  }

  protected graphQLFolderItem(): TFolderItemGraphQL {
    return this.get('graphQLFolderItem');
  }

  name(): string {
    return this.folderItem().name;
  }

  displayLabel(): Optional<string> {
    return this.folderItem().displayLabel;
  }

  folder(): StringIDFolder {
    return this.folderItem().folder;
  }

  projectFolderId(): Optional<string> {
    return this.folderItem().projectFolderId;
  }

  editURL(): string {
    return this.folderItem().editURL;
  }

  initialNote(): Optional<Note> {
    return this.folderItem().note;
  }

  permissions(): Permissions {
    return this.folderItem().permissions;
  }

  modifiedAt(): string {
    return this.folderItem().modified_at;
  }

  createdAt(): string {
    return this.folderItem().created_at;
  }

  archiveRecord(): ArchiveRecord | null {
    const graphQLFolderItem = this.graphQLFolderItem();
    return isArchived(graphQLFolderItem)
      ? ({purpose: getArchivePurpose(graphQLFolderItem)} as ArchiveRecord)
      : null;
  }

  authors(): Array<User> {
    return this.folderItem().authors;
  }

  setAuthors(authors: Array<User>): void {
    // @ts-expect-error: `BulkAssembly` does not support authors, so this line generically fails type checks.
    // However, we only expect the folder item types that support authors to call `setAuthors`. Ideally we
    // could assert that this method is only called if `TFolderItemGraphQL` contains `authors`, and remove
    // this TypeScript suppression.
    this.updateFolderItemAttributeInApolloCache('authors', restArrayToGraphQL('Author', authors));
  }

  initialBioentityAliasInfos(): Optional<Array<BioentityAliasInfo>> {
    return this.folderItem().bioentityAliasInfos;
  }

  thirdPartyStorageServiceData(): ThirdPartyStorageServiceData {
    return this.folderItem().thirdPartyStorageServiceData;
  }

  schemaValidationState(): Optional<SchemaValidationState> {
    return this.folderItem().schemaValidationState;
  }

  protected updateFolderItemAttributeInApolloCache<K extends keyof TFolderItemGraphQL>(
    key: K,
    value: TFolderItemGraphQL[K]
  ): void {
    writeQuery(this.getQueryWrapper(), this.getUpdatedQueryData({[key]: value}), this.queryVariables());
  }

  protected abstract getUpdatedQueryData(attrs: Partial<AccessibleFolderItemGraphQL>): TQuery;

  protected batchUpdateFolderItemAttributesInApolloCache(attrs: Partial<AccessibleFolderItemGraphQL>): void {
    writeQuery(this.getQueryWrapper(), this.getUpdatedQueryData(attrs), this.queryVariables());
  }

  setFolder(folder: StringIDFolder | NumberIDFolder): void {
    this.updateFolderItemAttributeInApolloCache(
      'folder',
      restToGraphQL('FolderSummary', withNonNullSource(toNumberIDFolder(folder)))
    );
  }

  clearProjectFolder(): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  setProjectFolder(folder: StringIDFolder | NumberIDFolder): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  setPermissions(permissions: Permissions): void {
    this.updateFolderItemAttributeInApolloCache('permissions', {
      __typename: 'PermissionsNode',
      ...permissions,
    } as TFolderItemGraphQL['permissions']);
  }

  private setArchiveRecordForCoreObjectArchivable(archiveRecord: ArchiveRecordGraphQL | null): void {
    const archiveDataKey = 'archiveData' as keyof TFolderItemGraphQL;
    const archiveDataValue =
      archiveRecord != null
        ? {
            __typename: 'ArchiveDataNode',
            archived: true,
            purpose: archiveRecord.purpose,
          }
        : {
            __typename: 'ArchiveDataNode',
            archived: false,
            purpose: null,
          };
    this.updateFolderItemAttributeInApolloCache(
      archiveDataKey,
      archiveDataValue as TFolderItemGraphQL[typeof archiveDataKey]
    );
  }

  private setArchiveRecordForHasArchiveRecord(archiveRecord: ArchiveRecordGraphQL | null): void {
    const archiveRecordKey = 'archiveRecord' as keyof TFolderItemGraphQL;
    this.updateFolderItemAttributeInApolloCache(
      archiveRecordKey,
      archiveRecord as TFolderItemGraphQL[typeof archiveRecordKey]
    );
  }

  setArchiveRecord(archiveRecord: ArchiveRecordGraphQL | null): void {
    const graphQLFolderItem = this.graphQLFolderItem();
    if (graphQLFolderItem.hasOwnProperty('archiveData')) {
      this.setArchiveRecordForCoreObjectArchivable(archiveRecord);
    } else if (graphQLFolderItem.hasOwnProperty('archiveRecord')) {
      this.setArchiveRecordForHasArchiveRecord(archiveRecord);
    } else {
      assertAndContinue(
        false,
        'archiveRecord called but neither ArchivableInterface nor HasArchiveRecordInterface is implemented'
      );
    }
  }

  setSchemaValidationState(schemaValidationState: Optional<SchemaValidationState>): void {
    // No-op here because only registry items have this attribute; handled in ApolloRegistryItemBase
  }

  setNameAndDisplayLabel(name: string, displayLabel: string): void {
    this.updateFolderItemAttributeInApolloCache('name', name);
    // Only registry items have displayLabel; this is handled in ApolloRegistryItemBase
  }
}

export default ApolloFolderItemBase;
